import React, {useCallback, useState, type KeyboardEvent, type MouseEvent, type RefObject} from 'react';
import {ReactComponent as ScreenshotIcon} from '@icons/Screenshot.svg';
import {Spinner} from '@ui/Button';
import {useAppDispatch} from '@hooks/app';
import {handleError, handleSuccess} from '@store/app.slice';
import {captureElementScreenshotBlob} from '@utils/captureElementScreenshot';
import {
    copyImageToClipboard,
    isClipboardImageWriteSupported,
    isCopyImageInFlightError,
} from '@utils/copyImageToClipboard';
import styles from './ScreenshotRestsButton.module.scss';

interface ScreenshotRestsButtonProps {
    targetRef: RefObject<HTMLElement | null>;
}

export const ScreenshotRestsButton = ({targetRef}: ScreenshotRestsButtonProps) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    const handleClick = useCallback(async () => {
        if (loading) {
            return;
        }

        const element = targetRef.current;

        if (!element) {
            dispatch(handleError({
                message: 'Область для скриншота не найдена. Обновите страницу и попробуйте снова',
                errors: [],
            }));
            return;
        }

        if (!isClipboardImageWriteSupported()) {
            dispatch(handleError({message: 'Копирование изображения не поддерживается', errors: []}));
            return;
        }

        setLoading(true);

        try {
            await copyImageToClipboard(captureElementScreenshotBlob(element));

            dispatch(handleError(null));
            dispatch(handleSuccess({message: 'Скриншот скопирован'}));
        } catch (error) {
            if (isCopyImageInFlightError(error)) {
                return;
            }

            console.error(error);
            dispatch(handleSuccess(null));
            dispatch(handleError({message: 'Ошибка создания скриншота', errors: []}));
        } finally {
            setLoading(false);
        }
    }, [dispatch, loading, targetRef]);

    const handleActivate = useCallback((event: MouseEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        void handleClick();
    }, [handleClick]);

    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            handleActivate(event);
        }
    }, [handleActivate]);

    return (
        <a
            href="#"
            role="button"
            className={styles.button}
            aria-label="Сделать скриншот"
            aria-disabled={loading}
            tabIndex={loading ? -1 : 0}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
        >
            <ScreenshotIcon className={styles.icon} aria-hidden/>
            <span className={styles.label}>
                <span className={loading ? styles.labelTextLoading : undefined}>
                    Сделать скриншот
                </span>
                {loading && (
                    <span className={styles.labelSpinner} aria-hidden>
                        <Spinner/>
                    </span>
                )}
            </span>
        </a>
    );
};
